import React, { forwardRef, useCallback, useMemo, useState } from "react";
import Select, { components } from "react-select";

import { KEYBOARD_KEYS } from "@constants/global.constants";

import { isNullOrUndefined } from "@utils/appUtils";

import WPInputHelperText from "@components/WPInputHelperText";
import WPInputLabel from "@components/WPInputLabel";

import AccordionDownArrowIcon from "@common/icons/AccordionDownArrowIcon";
import AccordionUpArrowIcon from "@common/icons/AccordionUpArrowIcon";
import CircularLoader from "@common/styles/components/circularLoader";

import "./WPDropdown.scss";

const CustomDropdownIndicator = props =>
  components.DropdownIndicator && (
    <components.DropdownIndicator {...props}>
      {props.selectProps.menuIsOpen ? <AccordionUpArrowIcon /> : <AccordionDownArrowIcon />}
    </components.DropdownIndicator>
  );

const CustomOption = ({ children, ...props }) => (
  <div id={`dropdonw-option-${props.data.value}`} className={props.isDisabled ? "disabled-option" : ""}>
    <components.Option {...props}>{children}</components.Option>
  </div>
);

const LoadingIndicator = () => <CircularLoader size='20px' />;

const CustomPlaceholder = ({ children, innerProps, ...props }) => (
  <components.Placeholder
    {...props}
    innerProps={{ ...innerProps, title: typeof children === "string" ? children : undefined }}
  >
    {children}
  </components.Placeholder>
);

const WPDropdown = forwardRef((props, ref) => {
  const {
    id,
    name,
    value,
    label,
    options,
    defaultValue,
    getOptionLabel,
    getOptionValue,
    onChange,
    isMulti,
    disabled,
    tabIndex,
    isLoading,
    menuPosition = "fixed",
    isOptionDisabled,
    formatGroupLabel,
    placeholder = "",
    isError,
    helperText,
    required,
    filterOption,
    className,
  } = props;
  const [menuIsOpen, setMenuIsOpen] = useState(false);

  const handleChange = selectedOption => {
    onChange(selectedOption, name);
  };

  const defaultFormatGroupLabel = data => (
    <div className='select__grouping'>
      <span>{data.label}</span>
    </div>
  );

  const optionValueGetter = useMemo(() => getOptionValue || (opt => opt.value), [getOptionValue]);

  const findOption = val => options?.find(opt => optionValueGetter?.(opt) === val) || val;

  const optionValue = useMemo(() => {
    if (isNullOrUndefined(value)) return value;
    if (Array.isArray(value)) {
      return value.map(v => (typeof v === "object" ? v : findOption(v)));
    }
    return typeof value === "object" ? value : findOption(value);
  }, [value, options, optionValueGetter]);

  const handleKeyDown = useCallback(event => {
    const { key } = event;
    if (key === KEYBOARD_KEYS.ARROWUP || key === KEYBOARD_KEYS.ARROWDOWN) {
      event.stopPropagation();
    }
  }, []);

  return (
    <div className={`dropdown-container ${className || ""}`}>
      {label && <WPInputLabel label={label} disabled={disabled} required={required} />}
      <Select
        ref={ref}
        id={id || "dropdown_id"}
        name={name || id || "dropdown_name"}
        defaultValue={defaultValue}
        value={optionValue ?? ""}
        isDisabled={disabled || isLoading}
        isMulti={isMulti}
        closeMenuOnSelect={!isMulti}
        onChange={handleChange}
        options={options}
        className={`basic-multi-select${isError ? " dropdown-error" : ""}`}
        classNamePrefix='select'
        placeholder={placeholder}
        inputId={id + "_input" || "dropdown_input"}
        components={{
          DropdownIndicator: CustomDropdownIndicator,
          Option: CustomOption,
          LoadingIndicator,
          Placeholder: CustomPlaceholder,
        }}
        filterOption={filterOption}
        onMenuOpen={() => setMenuIsOpen(true)}
        menuPosition={menuPosition}
        onMenuClose={() => setMenuIsOpen(false)}
        menuIsOpen={menuIsOpen}
        formatGroupLabel={formatGroupLabel || defaultFormatGroupLabel}
        getOptionLabel={getOptionLabel}
        getOptionValue={getOptionValue}
        tabIndex={tabIndex}
        isOptionDisabled={isOptionDisabled}
        isLoading={isLoading}
        onKeyDown={handleKeyDown}
        styles={{
          menuPortal: base => ({
            ...base,
            zIndex: 9999,
          }),
        }}
        menuPortalTarget={document.body}
      />
      {isError && <WPInputHelperText position='static' isError={isError} text={helperText} />}
    </div>
  );
});

WPDropdown.displayName = "WPDropdown";

export default WPDropdown;
